import { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { gaEvent, gaCartItem } from '../lib/analytics';
import { fbTrackDual } from '../lib/pixel';

const CartContext = createContext();

// Helper to get initial cart from localStorage
const getInitialCart = () => {
  if (typeof window === 'undefined') return [];
  try {
    const savedCart = localStorage.getItem('dyssc_cart');
    if (savedCart) {
      const parsedCart = JSON.parse(savedCart);
      if (Array.isArray(parsedCart)) {
        return parsedCart;
      }
    }
  } catch (e) {
    console.error('Error loading cart:', e);
  }
  return [];
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(getInitialCart);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('dyssc_cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product, quantity = 1) => {
    gaEvent('add_to_cart', {
      currency: 'INR',
      value: product.priceINR * quantity,
      items: [gaCartItem({ productId: product.slug, name: product.name, priceINR: product.priceINR }, quantity)],
    });
    fbTrackDual('AddToCart', {
      content_type: 'product',
      content_ids: [String(product.slug)],
      content_name: product.name,
      currency: 'INR',
      value: product.priceINR * quantity,
      contents: [{ id: String(product.slug), quantity, item_price: product.priceINR }],
      num_items: quantity,
    });
    const inCart = cart.some(item => item.productId === product.slug);
    if (inCart) toast.info('Updated quantity in cart');
    else toast.success(`${product.name} added to cart!`, { id: `added-${product.slug}` });
    setCart(prevCart => {
      const existing = prevCart.find(item => item.productId === product.slug);
      
      if (existing) {
        return prevCart.map(item =>
          item.productId === product.slug
            ? { ...item, qty: item.qty + quantity }
            : item
        );
      } else {
        return [
          ...prevCart,
          {
            productId: product.slug,
            name: product.name,
            priceINR: product.priceINR,
            basePriceINR: product.basePriceINR || null,
            discountPercent: product.discountPercent || 0,
            compareAtINR: product.compareAtINR || null,
            qty: quantity,
            image: product.images[0]
          }
        ];
      }
    });
    setIsCartOpen(true);
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    setCart(prevCart =>
      prevCart.map(item =>
        item.productId === productId ? { ...item, qty: quantity } : item
      )
    );
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.productId !== productId));
    toast.info('Item removed from cart');
  };

  const clearCart = () => {
    setCart([]);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + item.priceINR * item.qty, 0);
  };

  // GST-exclusive base total BEFORE product discounts
  const getCartBaseTotal = () => {
    return cart.reduce((total, item) => {
      const base = item.basePriceINR || Math.round(item.priceINR / 1.18);
      return total + base * item.qty;
    }, 0);
  };

  // GST-exclusive taxable value of the cart (per-unit: base − discount; legacy items back-derive)
  const getCartTaxable = () => {
    return cart.reduce((total, item) => {
      const base = item.basePriceINR || Math.round(item.priceINR / 1.18);
      const discAmt = item.basePriceINR && item.discountPercent > 0
        ? Math.round((base * item.discountPercent) / 100)
        : 0;
      return total + (base - discAmt) * item.qty;
    }, 0);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.qty, 0);
  };

  const value = {
    cart,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    getCartTotal,
    getCartBaseTotal,
    getCartTaxable,
    getCartCount,
    isCartOpen,
    setIsCartOpen
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
